// Facilitator deck for "Finance for Non-Finance Managers" (FIN-NFM-001), the
// three-day classroom programme. Condensed from the course content in
// src/data/courses/finance/nfmPart1.js and nfmPart2.js (Modules 0 to 10 and
// the Capstone). Timings follow the three-day timetable in
// facilitator-notes/finance-for-non-finance-managers.md, and the talking
// points in each slide's `notes` draw on its teaching sequence,
// misconceptions and debrief points. Every Ubuntu Manufacturing figure comes
// from the Dataset A files in public/course-files/finance-for-non-finance-
// managers/ (Ubuntu is fictional). Rates are training assumptions. Model
// answers stay in `notes` (presenter view only), never on projected slides.

import { applyOkiruTemplate } from '../okiruPresentationTemplate';

// A figures table inserted on a lead slide (canvas 1280 x 720). The height
// follows the row count so short tables are not stretched.
const table = (cells, fontSize = 20) => [
  {
    id: 't1', type: 'table', x: 80, y: 190, w: 1120,
    h: Math.min(420, cells.length * 52),
    header: true, cells, style: { fontSize },
  },
];

export const manifest = applyOkiruTemplate(
  {
    id: 'finance-for-non-finance-managers',
    title: 'Finance for Non-Finance Managers',
    subtitle: 'Okiru Learn · Three-day classroom programme',
    introDurationMin: 0,
    modules: [
      { index: 0, name: 'Day 1 · Module 0: Welcome and Financial Confidence', start: '09:00', durationMin: 30 },
      { index: 1, name: 'Day 1 · Module 1: Financial Statements Without the Jargon', start: '09:30', durationMin: 75 },
      { index: 2, name: 'Day 1 · Module 2: Reading the Income Statement', start: '11:00', durationMin: 90 },
      { index: 3, name: 'Day 1 · Module 3: Reading the Balance Sheet', start: '13:15', durationMin: 75 },
      { index: 4, name: 'Day 1 · Module 4: Cash Flow and Working Capital', start: '14:45', durationMin: 90 },
      { index: 5, name: 'Day 2 · Module 5: Costs, Budgeting and Variance Analysis', start: '09:15', durationMin: 135 },
      { index: 6, name: 'Day 2 · Module 6: Ratio Analysis for Managers', start: '11:45', durationMin: 75 },
      { index: 7, name: 'Day 2 · Module 7: Investment Decisions', start: '13:45', durationMin: 90 },
      { index: 8, name: 'Day 2 and Day 3 · Module 8: Planning and Forecasting', start: '15:30', durationMin: 105 },
      { index: 9, name: 'Day 3 · Module 9: Management Accounts and KPIs', start: '10:15', durationMin: 60 },
      { index: 10, name: 'Day 3 · Module 10: Financial Risk, Controls and Governance', start: '11:30', durationMin: 60 },
      { index: 11, name: 'Day 3 · Capstone: Run the Business Review', start: '13:15', durationMin: 165 },
    ],
  },
  'okiru-light',
);

export const slides = [
  // -------------------------------------------------------------------
  // Opening
  // -------------------------------------------------------------------
  {
    id: 1, layout: 'title',
    kicker: 'Okiru · Three-day classroom programme',
    title: 'Finance for Non-Finance Managers',
    lead: 'Understand the numbers, ask better questions and make better management decisions.',
    meta: ['Three days · Modules 0 to 10 and a capstone', 'One running case: Ubuntu Manufacturing (fictional)', 'No accounting qualification needed'],
    notes: 'Welcome the group and introduce yourself. The course is for managers who need to understand financial statements, budgets, cash flow and forecasts without becoming accountants. A calculator and comfort with percentages is all they need.',
  },
  {
    id: 2, layout: 'statement',
    text: 'You do not need to become an accountant. You need to know where your decisions land in the numbers, and which questions to ask.',
    footnote: 'Every operating decision lands somewhere in revenue, cost, cash, assets or liabilities, whether or not anyone from finance is in the room.',
    notes: 'This is the course promise. Ask: who in the room approved a purchase, a hire, a discount or overtime last month? Every one of those decisions changed the numbers. Finance records the result; managers make the decision.',
  },
  {
    id: 3, layout: 'cards',
    kicker: 'The three days', title: 'What the three days cover',
    columns: 3,
    cards: [
      { title: 'Day 1: Understand the numbers', description: 'The three statements, the Income Statement, the Balance Sheet, cash flow and working capital.', tag: 'Modules 0 to 4' },
      { title: 'Day 2: Use the numbers', description: 'Cost behaviour, budgets and variances, ratios, investment decisions and forecasting.', tag: 'Modules 5 to 8' },
      { title: 'Day 3: Plan and run the business', description: 'Scenarios, management accounts and KPIs, risk and controls, then the capstone review.', tag: 'Modules 8 to 10 and Capstone' },
    ],
    notes: 'Walk the arc of the programme: read, then use, then plan and run. Module 4 (cash and working capital) is the heart of Day 1 and the capstone is the heart of Day 3. Everything uses one case company so the story builds day by day.',
  },
  {
    id: 4, layout: 'cards',
    kicker: 'How the days run', title: 'How each day works',
    columns: 2,
    cards: [
      { title: '09:00 to 16:30', description: 'Morning tea, a 45-minute lunch and afternoon tea. 375 minutes of contact time a day.' },
      { title: 'Explain, show, calculate', description: 'Then interpret, decide and apply. Every module ends with an activity on real figures.' },
      { title: 'Fact, judgement, rule', description: 'Facts come from the figures. Judgements are arguable. Rules are legal or reporting requirements.' },
      { title: 'Files and calculators', description: 'Ubuntu datasets, templates and calculators stay available in Okiru Learn before and after each day.' },
    ],
    notes: 'Set expectations on timing and breaks. Introduce the six-step learning sequence: explain, show, calculate, interpret, decide, apply. Stress the fact, judgement and rule distinction now; the course comes back to it constantly. Point learners to the downloadable datasets and templates.',
  },
  {
    id: 5, layout: 'lead',
    kicker: 'The case company', title: 'Meet Ubuntu Manufacturing (Pty) Ltd',
    lead: 'A fictional South African manufacturer and distributor of commercial cleaning equipment.',
    body: 'Floor scrubbers, pressure washers and vacuum systems. About 60% of sales on credit. Imports USD-priced motors, manufactures locally, holds stock and runs a delivery fleet. Year end: 28 February.',
    templateAsset: 'team-collaboration', imageChrome: 'none',
    notes: 'Ubuntu is fictional and created for this course. Ask the group to predict what the description tells us before any numbers: credit sales mean receivables, stock means inventory, imports mean exchange rate exposure, a fleet and factory mean equipment. FY2026 means the year ended 28 February 2026. They will test each prediction against the figures.',
  },
  {
    id: 6, layout: 'agenda',
    kicker: 'Day 1', title: 'Day 1: Understand the numbers',
    columns: [
      [
        { time: '09:00', label: 'Module 0: Welcome and financial confidence' },
        { time: '09:30', label: 'Module 1: Financial statements without the jargon' },
        { time: '10:45', label: 'Tea' },
        { time: '11:00', label: 'Module 2: Reading the Income Statement' },
        { time: '12:30', label: 'Lunch' },
      ],
      [
        { time: '13:15', label: 'Module 3: Reading the Balance Sheet' },
        { time: '14:30', label: 'Tea' },
        { time: '14:45', label: 'Module 4: Cash flow and working capital' },
        { time: '16:15', label: 'Day 1 wrap: exit ticket' },
      ],
    ],
    notes: 'Day 1 moves from the three statements to cash. If you run late, protect Module 4 and compress Modules 1 and 3 first. The exit ticket at 16:15 asks for one number learners now understand and one they want to challenge.',
  },

  // -------------------------------------------------------------------
  // Module 0: Welcome and Financial Confidence
  // -------------------------------------------------------------------
  {
    id: 7, layout: 'intro', module: 0,
    kicker: 'Day 1 · Module 00', title: 'Welcome and Financial Confidence',
    lead: 'Every manager already makes financial decisions. This module shows where they land.',
    outcome: 'Recognise the financial consequences of the everyday decisions you already make, and know how this course and its case company work.',
    templateAsset: 'workshop-facilitator', imageChrome: 'none',
    notes: '30 minutes, 09:00 to 09:30. Open by asking each learner for one financial decision they made last month. Keep it light: the purpose is to lower anxiety and show they already speak some of the language.',
  },
  {
    id: 8, layout: 'cards', module: 0,
    kicker: 'Module 00', title: 'Finance is a management language',
    lead: 'Every decision changes revenue, cost, cash, assets or liabilities. Most change more than one.',
    columns: 3,
    cards: [
      { title: 'Hiring', description: 'Cost rises every month, usually for years.', tag: 'Cost and cash' },
      { title: 'Discounting', description: 'Revenue falls; the full discount comes off profit.', tag: 'Revenue' },
      { title: 'Ordering stock', description: 'Inventory rises; cash falls when the supplier is paid.', tag: 'Assets and cash' },
      { title: 'Buying equipment', description: 'Assets rise; cash falls, or a loan rises.', tag: 'Assets and liabilities' },
      { title: 'Customer credit', description: 'Receivables rise; the cash arrives later.', tag: 'Assets and cash' },
      { title: 'Supplier terms', description: 'Payables and the timing of cash change.', tag: 'Liabilities and cash' },
    ],
    notes: 'Use learners\' own examples rather than reading the cards. Misconception to challenge: "finance is the finance department\'s job". Every approved purchase order, hire or discount changes the numbers. Ask: which of your decisions affects cash but not profit in the month you make it?',
  },
  {
    id: 9, layout: 'comparison', module: 0,
    kicker: 'Module 00 · Mini case', title: 'The overtime request',
    lead: 'Nomvula is asked to approve another month of weekend overtime, roughly R95,000 (fictional).',
    columns: [
      { heading: 'Weak responses', items: ['Approve: overtime is the fastest fix', 'Refuse until the next budget cycle and ask the team to work faster'] },
      { heading: 'Stronger responses', items: ['Compare overtime with one or two permanent staff', 'Check what the contract pays for the extra work', 'Approve one more month while gathering the comparison'] },
    ],
    callout: { label: 'Three questions for any decision', text: 'How much, once-off or recurring? When does the cash move? What does it commit us to?' },
    notes: 'Ask the room what Nomvula should do before revealing the columns. The best answer compares the recurring overtime cost with permanent staff and checks what the client pays. Introduce the three questions: amount and recurrence, cash timing, commitment. Separate fact ("overtime cost R95,000") from judgement ("it was necessary").',
  },
  {
    id: 10, layout: 'exercise', module: 0,
    kicker: 'Activity 0.1', meta: 'Pairs · 10 min', title: 'My Financial Footprint',
    steps: [
      { title: 'Sort five sample decisions', detail: 'Hire, 60-day customer terms, bulk stock for a discount, a delivery vehicle, 45-day supplier terms.' },
      { title: 'Name the main effect', detail: 'Revenue, cost, cash, assets or liabilities. Then name what else it touches.' },
      { title: 'Map five of your own', detail: 'Decisions from your own role, against the same five headings.' },
    ],
    checkFor: 'most decisions have two or more effects, and the cash effect often arrives at a different time from the profit effect.',
    notes: 'Typical mapping: a hire is cost plus a monthly cash outflow; 60-day terms delay cash and raise receivables; bulk stock raises inventory and uses cash; a vehicle is an asset, with cash out now and depreciation later; 45-day supplier terms raise payables and keep cash longer. Accept reasonable alternatives if the learner explains the link. Time-saver: run the five sample decisions as a show of hands and set own-role mapping as pre-work.',
  },
  {
    id: 11, layout: 'lead', module: 0,
    kicker: 'Module 00 · Ubuntu at a glance', title: 'More sales, less profit, no cash',
    lead: '',
    extras: table([
      ['Item (fictional, R)', 'FY2025', 'FY2026', 'Change'],
      ['Revenue', 'R24,000,000', 'R27,600,000', '+15.0%'],
      ['Gross profit', 'R8,640,000', 'R9,384,000', '+8.6%'],
      ['Net profit', 'R1,358,000', 'R1,200,000', '−11.6%'],
      ['Net cash from operating activities', 'R1,578,000', 'R437,000', '−72.3%'],
      ['Trade receivables (year end)', 'R1,894,000', 'R2,813,000', '+48.5%'],
      ['Inventory (year end)', 'R1,683,000', 'R2,396,000', '+42.4%'],
      ['Closing cash, net of overdraft', 'R1,078,000', '−R335,000', '−R1,413,000'],
    ]),
    notes: 'Show the table and do not explain it yet: it is the hook for Modules 2 to 4. Ask what stands out. Sales grew 15%, net profit fell 11.6% and cash went from R1,078,000 in the bank to an overdraft of R335,000. Park "cash and profit move together"; Modules 1 and 4 deal with it.',
  },

  // -------------------------------------------------------------------
  // Module 1: Financial Statements Without the Jargon
  // -------------------------------------------------------------------
  {
    id: 12, layout: 'intro', module: 1,
    kicker: 'Day 1 · Module 01', title: 'Financial Statements Without the Jargon',
    lead: 'Three statements, three questions, one connected story.',
    outcome: 'Read the three financial statements as one connected story and explain why profit and cash differ.',
    templateAsset: 'thinking-learner', imageChrome: 'none',
    notes: '75 minutes, 09:30 to 10:45. Key message for the whole module: profit is not cash. Time-saver if running late: teach the equation with the Ubuntu balance sheet only and run three of the five accrual cases.',
  },
  {
    id: 13, layout: 'keyterm', module: 1,
    kicker: 'Lesson 1.1 · Key term', term: 'Assets = Liabilities + Equity',
    definition: 'Everything the business owns was funded by what it owes to others plus what belongs to its owners.',
    body: 'Rearranged: Equity = Assets − Liabilities. Equity is a residual claim, not a pile of cash.',
    callouts: [
      { label: 'Worked example', text: 'Cash R250,000 + inventory R500,000 + equipment R750,000 = assets R1,500,000. Loan R600,000 + creditors R300,000 = liabilities R900,000. Equity = R600,000.' },
      { label: 'Check', text: 'R1,500,000 = R900,000 + R600,000. Only R250,000 of the assets is cash.' },
    ],
    notes: 'Present Assets = Liabilities + Equity first, then the rearranged form. Do not lead with "A − L = E". Work the example on the board. Misconception: equity is cash the business holds. Ubuntu had R5,086,000 of equity and no cash at bank at 28 February 2026.',
  },
  {
    id: 14, layout: 'cards', module: 1,
    kicker: 'Lesson 1.2', title: 'Three statements, three questions',
    columns: 3,
    cards: [
      { title: 'Income Statement', description: 'Did we make a profit? Performance over a period.', tag: 'FY2026: net profit R1,200,000' },
      { title: 'Balance Sheet', description: 'What do we own and owe? Position at a date.', tag: 'Assets R11,759,000 at 28 Feb 2026' },
      { title: 'Cash Flow Statement', description: 'Where did cash come from and go? Movement over a period.', tag: 'Cash fell R1,413,000' },
    ],
    footer: 'Also called the Statement of Profit or Loss and the Statement of Financial Position.',
    notes: 'Introduce both names once, then use the plain names for the rest of the course. Ask: can a business have positive equity and no cash? Yes, Ubuntu in FY2026. The three questions are the debrief line for this lesson: how did we perform, what do we own and owe, where did the cash go?',
  },
  {
    id: 15, layout: 'numbered', module: 1,
    kicker: 'Lesson 1.2 · Worked example', title: 'Following a R100,000 credit sale',
    items: [
      { title: 'Day 1: the invoice', description: 'Ubuntu delivers a floor scrubber and invoices R100,000 on 30-day terms.' },
      { title: 'Income Statement', description: 'Revenue up R100,000 now; the scrubber\'s cost moves into cost of sales.' },
      { title: 'Balance Sheet', description: 'Receivables up R100,000; inventory down by the scrubber\'s cost.' },
      { title: 'Cash Flow Statement', description: 'No customer cash yet. The rise in receivables cancels the sale in operating cash.' },
      { title: 'Day 30: the customer pays', description: 'Cash up R100,000, receivables down R100,000. No Income Statement effect.' },
    ],
    notes: 'Trace the sale on the board across three columns. Ask before each step: what changes on this statement today? Equity also rises by the profit on the sale, via retained earnings. Misconception: revenue means money received. A credit sale is revenue before it is cash.',
  },
  {
    id: 16, layout: 'lead', module: 1,
    kicker: 'Lesson 1.3 · Accrual vs cash', title: 'Five everyday events: profit versus cash',
    lead: '',
    extras: table([
      ['Event', 'Income Statement', 'Cash'],
      ['Sales on 30-day terms', 'Revenue when goods are delivered', 'About 30 days later'],
      ['Annual insurance R180,000 paid upfront in March', 'R15,000 a month for 12 months', 'R180,000 out in March'],
      ['Supplier invoice not yet paid', 'Cost when goods are received', 'When the invoice is paid'],
      ['February salaries paid on 1 March', 'Expense in February', 'Cash out in March'],
      ['Depreciation of equipment', 'Expense every year of its life', 'None: cash left at purchase'],
    ], 19),
    notes: 'Run the five cases as questions before revealing each row. Ask: the insurance is paid in March, what does the March Income Statement show? Misconception: depreciation is a cash cost. It spreads the cost of an asset already paid for. Timing differences explain most profit-versus-cash gaps.',
  },
  {
    id: 17, layout: 'lead', module: 1,
    kicker: 'Lesson 1.2 · Ubuntu FY2026', title: 'The statements link and reconcile',
    lead: '',
    extras: table([
      ['Check (fictional, R)', 'Working', 'Result'],
      ['Retained earnings', 'R3,386,000 + R1,200,000 profit − R500,000 dividends', 'R4,086,000'],
      ['Closing cash', 'R1,078,000 − R1,413,000', '−R335,000 (overdraft)'],
      ['Equation FY2025', 'R5,749,000 liabilities + R4,386,000 equity', 'R10,135,000 assets'],
      ['Equation FY2026', 'R6,673,000 liabilities + R5,086,000 equity', 'R11,759,000 assets'],
    ]),
    notes: 'Ubuntu kept R700,000 of its profit in the business, yet cash fell by more than R1.4m. The statements are consistent, so this is not an error: something on the Balance Sheet consumed cash. Receivables and inventory are the first places to look, and Module 4 does exactly that.',
  },
  {
    id: 18, layout: 'exercise', module: 1,
    kicker: 'Activity 1.1', meta: 'Pairs · 10 min', title: 'Profit or cash?',
    steps: [
      { title: 'Start from Ubuntu\'s FY2026 profit', detail: 'Net profit R1,200,000, depreciation R780,000.' },
      { title: 'Deduct the rise in receivables', detail: 'Receivables grew R919,000. Ignore every other movement.' },
      { title: 'Explain the gap in one sentence', detail: 'Why is the cash from operations so different from profit?' },
    ],
    checkFor: 'the working reaches R1,061,000 and the explanation names timing, not an error.',
    notes: 'Answer: R1,200,000 + R780,000 − R919,000 = R1,061,000. Depreciation is added back because no cash moved. Optional extension: ask learners to find one prepayment and one accrual in their own department\'s costs.',
  },

  // -------------------------------------------------------------------
  // Module 2: Reading the Income Statement
  // -------------------------------------------------------------------
  {
    id: 19, layout: 'intro', module: 2,
    kicker: 'Day 1 · Module 02', title: 'Reading the Income Statement',
    lead: 'From revenue to net profit: where did the growth leak away?',
    outcome: 'Read an Income Statement from revenue to net profit, calculate the four core margins and turn them into questions for management.',
    templateAsset: 'problem-solving', imageChrome: 'none',
    notes: '90 minutes, 11:00 to 12:30. Ubuntu\'s FY2026 pattern, revenue up and every margin down, is the anchor. Time-saver: skip Company A vs B and calculate Ubuntu margins only.',
  },
  {
    id: 20, layout: 'keyterm', module: 2,
    kicker: 'Lessons 2.1 to 2.3 · Formulas', term: 'The four margins',
    definition: 'Each margin shows how much of each rand of revenue survives one more layer of cost.',
    body: 'Gross margin % = Gross profit ÷ Revenue × 100. EBITDA, operating (EBIT) and net margin follow the same pattern.',
    callouts: [
      { label: 'Layers', text: 'Revenue − cost of sales = gross profit. − operating expenses = EBITDA. − depreciation = EBIT. − interest and tax = net profit.' },
      { label: 'Ask first', text: 'What is normal for this business? Margins are questions, not scores.' },
    ],
    notes: 'Teach interpretation, not accounting theory. EBITDA is profit before interest, tax, depreciation and amortisation. Misconception: EBITDA is cash. It ignores working capital, tax, interest and capex; Ubuntu\'s EBITDA of R3,034,000 became operating cash flow of R437,000.',
  },
  {
    id: 21, layout: 'numbered', module: 2,
    kicker: 'Lesson 2.1 · Worked example', title: 'Gross margin, and two what-ifs',
    items: [
      { title: 'Revenue R10,000,000, cost of sales R6,500,000', description: 'Gross profit R3,500,000. Gross margin 35%.' },
      { title: 'Selling price falls 5%', description: 'Revenue R9,500,000, gross profit R3,000,000. Margin 31.6%.' },
      { title: 'Materials (60% of cost of sales) rise 8%', description: 'Cost up R312,000. Gross profit R3,188,000.' },
      { title: 'The lesson', description: 'Small price moves go straight to gross profit. Defend price.' },
    ],
    notes: 'Ask learners to calculate each what-if before you reveal it. A 5% price fall costs R500,000 of gross profit, all from the price change. For the materials case, R3,900,000 × 8% = R312,000. Ask what share of their own cost of sales is material.',
  },
  {
    id: 22, layout: 'lead', module: 2,
    kicker: 'Ubuntu Income Statement (1 of 2)', title: 'Revenue to EBITDA',
    lead: '',
    extras: table([
      ['Line (fictional, R)', 'FY2025', 'FY2026', 'Change'],
      ['Revenue', 'R24,000,000', 'R27,600,000', '+15.0%'],
      ['Cost of sales', 'R15,360,000', 'R18,216,000', '+18.6%'],
      ['Gross profit', 'R8,640,000', 'R9,384,000', '+8.6%'],
      ['Operating expenses (excl. depreciation)', 'R5,640,000', 'R6,350,000', '+12.6%'],
      ['EBITDA', 'R3,000,000', 'R3,034,000', '+1.1%'],
    ]),
    notes: 'Revenue grew R3.6m but EBITDA grew only R34,000. Ask: gross profit grew 8.6% while revenue grew 15%, what could explain the gap? Pricing, discounting, input costs, product mix. The FY2026 data does not say why cost of sales grew faster; that is a question for management, not a conclusion.',
  },
  {
    id: 23, layout: 'lead', module: 2,
    kicker: 'Ubuntu Income Statement (2 of 2)', title: 'EBITDA to net profit',
    lead: '',
    extras: table([
      ['Line (fictional, R)', 'FY2025', 'FY2026', 'Change'],
      ['EBITDA', 'R3,000,000', 'R3,034,000', '+1.1%'],
      ['Depreciation', 'R620,000', 'R780,000', '+25.8%'],
      ['Operating profit (EBIT)', 'R2,380,000', 'R2,254,000', '−5.3%'],
      ['Finance costs', 'R520,000', 'R610,000', '+17.3%'],
      ['Profit before tax', 'R1,860,000', 'R1,644,000', '−11.6%'],
      ['Income tax (27%, training assumption)', 'R502,000', 'R444,000', '−11.6%'],
      ['Net profit', 'R1,358,000', 'R1,200,000', '−11.6%'],
    ]),
    notes: 'Operating profit fell 5.3% but net profit fell 11.6%. Ask what sits between them: finance costs up R90,000, then tax. Depreciation and interest rose because of capex and new debt. This is negative operating leverage: revenue up 15%, EBIT down 5.3%.',
  },
  {
    id: 24, layout: 'lead', module: 2,
    kicker: 'Lesson 2.2 · Operating expenses', title: 'Which overheads grew fastest?',
    lead: '',
    extras: table([
      ['Line (fictional, R)', 'FY2025', 'FY2026', 'Change'],
      ['Salaries and wages (non-production)', 'R3,000,000', 'R3,420,000', '+14.0%'],
      ['Premises rent and utilities', 'R960,000', 'R1,020,000', '+6.3%'],
      ['Fleet and delivery', 'R610,000', 'R690,000', '+13.1%'],
      ['Marketing', 'R290,000', 'R310,000', '+6.9%'],
      ['Technology', 'R230,000', 'R260,000', '+13.0%'],
      ['Insurance', 'R160,000', 'R180,000', '+12.5%'],
      ['Professional fees and administration', 'R390,000', 'R470,000', '+20.5%'],
      ['Total operating expenses', 'R5,640,000', 'R6,350,000', '+12.6%'],
    ], 19),
    notes: 'Gross profit rose R744,000 and operating expenses rose R710,000, so overheads absorbed almost all the extra gross profit. Ask: does the growth in salaries and professional fees buy future revenue? That is the question to put to the executive team.',
  },
  {
    id: 25, layout: 'exercise', module: 2,
    kicker: 'Activity 2.1', meta: 'Groups · 30 min', title: 'Margin analysis',
    steps: [
      { title: 'Ubuntu, both years', detail: 'Calculate gross, EBITDA, operating and net margin for FY2025 and FY2026.' },
      { title: 'Company A and Company B', detail: 'Use Lesson 2.3 figures. Compare gross economics, overhead weight and debt.' },
      { title: 'Write three questions', detail: 'Questions for management, not a ranking of the companies.' },
    ],
    checkFor: 'every margin is read in pairs, and the answer says where between revenue and net profit the growth leaked away.',
    notes: 'Ubuntu answers: gross 36.0% to 34.0%, EBITDA 12.5% to 11.0%, operating 9.9% to 8.2%, net 5.7% to 4.3%. Company A: gross 45.0%, EBITDA 10.0%, net 3.7%, heavy overheads and finance costs. Company B: gross 25.0%, EBITDA 11.0%, net 6.2%, lean but little room for cost shocks. Mark the questions, not a winner.',
  },
  {
    id: 26, layout: 'lead', module: 2,
    kicker: 'Debrief · Lesson 2.3', title: 'Ubuntu margins over two years',
    lead: '',
    extras: table([
      ['Margin', 'FY2025', 'FY2026', 'Movement'],
      ['Gross margin', '36.0%', '34.0%', '−2.0 points'],
      ['EBITDA margin', '12.5%', '11.0%', '−1.5 points'],
      ['Operating margin', '9.9%', '8.2%', '−1.7 points'],
      ['Net margin', '5.7%', '4.3%', '−1.4 points'],
    ]),
    notes: 'Show after the activity. Every margin fell, and the first fall is at gross level. Misconception: a 2-point fall is small. On R27,600,000 of revenue, 2 points of gross margin is R552,000 of gross profit. Also separate percentage points from percentage change: 36.0% to 34.0% is a 2-point fall, a 5.6% relative fall.',
  },
  {
    id: 27, layout: 'lead', module: 2,
    kicker: 'Lesson 2.4 · Red flags', title: 'Red flags applied to Ubuntu FY2026',
    lead: '',
    extras: table([
      ['Red flag', 'Ubuntu evidence', 'Present?'],
      ['Revenue rising, gross margin falling', 'Revenue +15.0%; gross margin 36.0% to 34.0%', 'Yes'],
      ['Overheads rising faster than gross profit', 'Operating expenses +12.6%; gross profit +8.6%', 'Yes'],
      ['Interest costs rising', 'Finance costs R520,000 to R610,000 (+17.3%)', 'Yes'],
      ['Negative operating leverage', 'Revenue +15.0%; EBIT R2,380,000 to R2,254,000', 'Yes'],
      ['Once-off income, one customer, discounts', 'Not visible in the annual figures', 'Unknown: ask'],
    ], 19),
    notes: 'A red flag is a question, not a verdict; each one has a possible innocent explanation. Debrief: Ubuntu\'s leak started at gross margin and was widened by overhead growth. Customer dependence and discounts need management information the annual statements do not show.',
  },

  // -------------------------------------------------------------------
  // Module 3: Reading the Balance Sheet
  // -------------------------------------------------------------------
  {
    id: 28, layout: 'intro', module: 3,
    kicker: 'Day 1 · Module 03', title: 'Reading the Balance Sheet',
    lead: 'A snapshot of what the business owns, what it owes and how it is funded.',
    outcome: 'Read a Balance Sheet to judge what a business owns, what it owes, how easily it can pay short-term obligations and how its funding is changing.',
    templateAsset: 'thinking-learner', imageChrome: 'none',
    notes: '75 minutes, 13:15 to 14:30. The key message: the Balance Sheet shows where the profit went. Time-saver: combine Lessons 3.1 and 3.2 into one 15-minute walk through the Ubuntu Balance Sheet and keep the ratios and red flags.',
  },
  {
    id: 29, layout: 'lead', module: 3,
    kicker: 'Lesson 3.1 · Assets', title: 'Ubuntu assets at year end',
    lead: '',
    extras: table([
      ['Asset (fictional, R)', '28 Feb 2025', '28 Feb 2026', 'Change', 'Change %'],
      ['Property, plant and equipment', 'R5,480,000', 'R6,550,000', '+R1,070,000', '+19.5%'],
      ['Inventory', 'R1,683,000', 'R2,396,000', '+R713,000', '+42.4%'],
      ['Trade receivables', 'R1,894,000', 'R2,813,000', '+R919,000', '+48.5%'],
      ['Cash at bank', 'R1,078,000', 'R0', '−R1,078,000', '−100%'],
      ['Total current assets', 'R4,655,000', 'R5,209,000', '+R554,000', '+11.9%'],
      ['Total assets', 'R10,135,000', 'R11,759,000', '+R1,624,000', '+16.0%'],
    ], 19),
    notes: 'Ask: which grew faster, receivables or revenue? Receivables +48.5% against revenue +15.0%. PPE rose because capex of R1,850,000 was more than twice depreciation of R780,000, a sign of expansion. Misconception: more assets means a stronger business. Assets that are slow to convert can be a warning.',
  },
  {
    id: 30, layout: 'lead', module: 3,
    kicker: 'Lesson 3.2 · Liabilities and equity', title: 'What Ubuntu owes, and when',
    lead: '',
    extras: table([
      ['Line (fictional, R)', '28 Feb 2025', '28 Feb 2026', 'Change'],
      ['Trade payables', 'R1,767,000', 'R1,747,000', '−R20,000'],
      ['Accruals', 'R410,000', 'R455,000', '+R45,000'],
      ['Tax payable', 'R172,000', 'R236,000', '+R64,000'],
      ['Current portion of bank loan', 'R600,000', 'R600,000', 'No change'],
      ['Bank overdraft', 'R0', 'R335,000', '+R335,000'],
      ['Total current liabilities', 'R2,949,000', 'R3,373,000', '+R424,000'],
      ['Bank loan (non-current)', 'R2,800,000', 'R3,300,000', '+R500,000'],
      ['Total liabilities', 'R5,749,000', 'R6,673,000', '+R924,000'],
      ['Total equity', 'R4,386,000', 'R5,086,000', '+R700,000'],
    ], 18),
    notes: 'The bank loan rose from R3,400,000 to R3,900,000: R1,100,000 drawn, R600,000 repaid. Add the overdraft and interest-bearing debt is R4,235,000. Misconception: retained earnings is money in the bank. Retained earnings rose R700,000 while cash fell R1,413,000. Match funding to the asset: long-life equipment belongs on term debt, not the overdraft.',
  },
  {
    id: 31, layout: 'keyterm', module: 3,
    kicker: 'Lesson 3.3 · Formulas', term: 'Current ratio and quick ratio',
    definition: 'Current ratio = Current assets ÷ Current liabilities. Quick ratio = (Cash + Trade receivables) ÷ Current liabilities.',
    body: 'The quick ratio leaves out inventory. It asks: can we pay without selling stock?',
    callouts: [
      { label: 'Context first', text: 'A current ratio of 1.1 is not automatically bad. Cash conversion, supplier terms, seasonality and industry matter.' },
      { label: 'Overdraft', text: 'An overdraft is a current liability, not negative cash, in these ratios.' },
    ],
    notes: 'Stress that ratios depend on context and trend. Misconception: a current ratio above 1.5 is healthy. Ubuntu\'s FY2026 current ratio is 1.54 and it has an overdraft. Set up the activity next: learners calculate both ratios for both years.',
  },
  {
    id: 32, layout: 'exercise', module: 3,
    kicker: 'Activity 3.1', meta: 'Pairs · 20 min', title: 'Liquidity and balance sheet red flags',
    steps: [
      { title: 'Calculate both ratios', detail: 'Current and quick ratio for FY2025 and FY2026.' },
      { title: 'Explain the gap', detail: 'Why did the quick ratio fall more than the current ratio?' },
      { title: 'List the red flags', detail: 'Use the eight balance sheet red flags. Tie each one to an Ubuntu figure.' },
    ],
    checkFor: 'each ratio is read with its trend and each red flag is tied to a specific number.',
    notes: 'Answers: current ratio 1.58 to 1.54; quick ratio 1.01 to 0.83. The current ratio barely moved because cash was replaced by receivables and inventory. Strong learners flag receivables +48.5%, inventory +42.4%, cash to nil with a R335,000 overdraft, current liabilities +14.4% and debt +R835,000. Not present: negative equity, related-party balances, intangibles.',
  },
  {
    id: 33, layout: 'lead', module: 3,
    kicker: 'Debrief · Lesson 3.3', title: 'Ubuntu liquidity and debt',
    lead: '',
    extras: table([
      ['Measure', 'FY2025', 'FY2026', 'FY2026 working'],
      ['Current ratio', '1.58', '1.54', 'R5,209,000 ÷ R3,373,000'],
      ['Quick ratio', '1.01', '0.83', '(R0 + R2,813,000) ÷ R3,373,000'],
      ['Interest-bearing debt', 'R3,400,000', 'R4,235,000', 'R3,900,000 loan + R335,000 overdraft'],
      ['Debt growth vs EBITDA growth', '', '+24.6% vs +1.1%', 'Debt rising without profit'],
    ], 19),
    notes: 'Show after the activity. Without selling stock, Ubuntu covers 83 cents of each rand of current liabilities, and that depends on collecting R2.8m of receivables that are themselves slowing. The quick ratio exposes what the current ratio hides.',
  },
  {
    id: 34, layout: 'lead', module: 3,
    kicker: 'Lesson 3.4 · Red flags', title: 'Balance sheet red flags at Ubuntu',
    lead: '',
    extras: table([
      ['Red flag', 'Ubuntu evidence FY2025 to FY2026', 'Assessment'],
      ['Receivables faster than revenue', 'Receivables +48.5%; revenue +15.0%', 'Present'],
      ['Inventory growth', 'Inventory +42.4%; cost of sales +18.6%', 'Present: ageing needed'],
      ['Current liabilities rising', '+14.4%, including a new R335,000 overdraft', 'Present, moderate'],
      ['Cash falling', 'R1,078,000 to an overdraft of R335,000', 'Present'],
      ['Debt without matching profit', 'Debt +24.6%; EBITDA +1.1%', 'Present'],
      ['Negative equity', 'Equity R5,086,000 and growing', 'Not present'],
      ['Related-party balances', 'None shown', 'Not visible: confirm'],
      ['Intangible assets', 'None recorded', 'Not present'],
    ], 18),
    notes: 'Read the Balance Sheet as a story: the profit went into receivables, inventory and equipment. Ratios need a trend and a comparison with what is normal for the business. Ask learners to write two questions for the FD from this table.',
  },

  // -------------------------------------------------------------------
  // Module 4: Cash Flow and Working Capital
  // -------------------------------------------------------------------
  {
    id: 35, layout: 'intro', module: 4,
    kicker: 'Day 1 · Module 04', title: 'Cash Flow and Working Capital',
    lead: 'Why a profitable business runs out of cash, and what managers can do about it.',
    outcome: 'Read a Cash Flow Statement in context, explain why a profitable business can run out of cash, and calculate how working capital decisions release or absorb cash.',
    templateAsset: 'problem-solving', imageChrome: 'none',
    notes: '90 minutes, 14:45 to 16:15. The defining module of the course: protect its time. Do not teach a single "healthy pattern" for cash flow; ask what is normal for a growing manufacturer that is investing.',
  },
  {
    id: 36, layout: 'lead', module: 4,
    kicker: 'Lesson 4.1 · Operating activities', title: 'From profit to operating cash',
    lead: '',
    extras: table([
      ['Line (fictional, R)', 'FY2025', 'FY2026'],
      ['Net profit', 'R1,358,000', 'R1,200,000'],
      ['Add back: depreciation', 'R620,000', 'R780,000'],
      ['(Increase) in trade receivables', '−R304,000', '−R919,000'],
      ['(Increase) in inventory', '−R262,000', '−R713,000'],
      ['Increase / (decrease) in trade payables', '+R84,000', '−R20,000'],
      ['Increase in accruals', '+R30,000', '+R45,000'],
      ['Increase in tax payable', '+R52,000', '+R64,000'],
      ['Net cash from operating activities', 'R1,578,000', 'R437,000'],
    ], 19),
    notes: 'Before showing the investing and financing lines, ask learners to list the causes of the cash fall from this table. Misconception: operating cash flow should be close to profit. In a growing business with slower collections it can be far lower: R437,000 on R1,200,000 of profit. Receivables and inventory alone absorbed R1,632,000, more than the whole net profit.',
  },
  {
    id: 37, layout: 'lead', module: 4,
    kicker: 'Lesson 4.1 · Investing and financing', title: 'From operating cash to an overdraft',
    lead: '',
    extras: table([
      ['Line (fictional, R)', 'FY2025', 'FY2026'],
      ['Net cash from operating activities', 'R1,578,000', 'R437,000'],
      ['Purchase of property, plant and equipment', '−R900,000', '−R1,850,000'],
      ['New bank loan drawn', 'R0', 'R1,100,000'],
      ['Bank loan repaid', '−R600,000', '−R600,000'],
      ['Dividends paid', '−R400,000', '−R500,000'],
      ['Net change in cash', '−R322,000', '−R1,413,000'],
      ['Opening cash (net of overdraft)', 'R1,400,000', 'R1,078,000'],
      ['Closing cash (net of overdraft)', 'R1,078,000', '−R335,000'],
    ], 19),
    notes: 'The new loan covered the repayment and the dividend but nothing more, so the capex was effectively funded by running cash down to an overdraft. Ask: the business borrowed R1,100,000 and still ended overdrawn, why? Misconception: the overdraft is the problem. It is the symptom; working capital, capex and dividends are the causes. Tax was not a cause: tax paid fell from R450,000 to R380,000.',
  },
  {
    id: 38, layout: 'keyterm', module: 4,
    kicker: 'Lessons 4.3 and 4.4 · Formulas', term: 'Cash conversion cycle',
    definition: 'Debtor days + Inventory days − Creditor days. The number of days cash is tied up in operations.',
    body: 'Debtor days = Closing trade receivables ÷ Credit sales × 365. Inventory days = Closing inventory ÷ Cost of sales × 365.',
    callouts: [
      { label: 'Creditor days (course convention)', text: 'Closing trade payables ÷ Cost of sales × 365. Purchases data is not available, so cost of sales is the base.' },
      { label: 'Ubuntu credit sales', text: '60% of revenue: R16,560,000 in FY2026.' },
    ],
    notes: 'State the creditor days convention once and move on. Ask which of the three day-counts each learner\'s department can influence: sales and credit control own debtor days, operations and procurement own inventory days, procurement with finance own creditor days. Then set the calculation.',
  },
  {
    id: 39, layout: 'exercise', module: 4,
    kicker: 'Activity 4.1', meta: 'Groups · 35 min', title: 'Why did Ubuntu run out of cash?',
    steps: [
      { title: 'Split the R1,413,000 fall', detail: 'How much came from working capital, from investment and from payments to funders?' },
      { title: 'Calculate the days', detail: 'Debtor, inventory and creditor days and the CCC, FY2025 and FY2026.' },
      { title: 'Size the release', detail: 'Cash released by returning to FY2025 days on FY2026 volumes.' },
    ],
    checkFor: 'each component is valued on its own base (credit sales for debtors, cost of sales for the others) and the causes are backed by the cash flow lines.',
    notes: 'Working capital absorbed R1,652,000 (919,000 + 713,000 + 20,000). Capex R1,850,000, partly funded by the R1,100,000 loan; repayments R600,000; dividends R500,000. Days: 48 + 40 − 42 = 46 in FY2025; 62 + 48 − 35 = 75 in FY2026. Common error: 29 CCC days times one daily value; give method credit but show the correct split.',
  },
  {
    id: 40, layout: 'lead', module: 4,
    kicker: 'Debrief · Lesson 4.4', title: 'Ubuntu working capital days',
    lead: '',
    extras: table([
      ['Measure', 'FY2025', 'FY2026', 'Change'],
      ['Debtor days', '48', '62', '+14 days'],
      ['Inventory days', '40', '48', '+8 days'],
      ['Creditor days', '42', '35', '−7 days'],
      ['Cash conversion cycle', '46', '75', '+29 days'],
    ]),
    notes: 'Customers take about two months to pay, stock sits about seven weeks and suppliers are paid in about five weeks. Every one of the three moved in the direction that uses more cash. Misconception: paying suppliers later is free cash. Ubuntu actually moved the other way, from 42 to 35 creditor days.',
  },
  {
    id: 41, layout: 'lead', module: 4,
    kicker: 'Debrief · Lesson 4.2', title: 'What the slipping days cost in cash',
    lead: '',
    extras: table([
      ['Item (FY2026 volumes)', 'At FY2025 days', 'Actual FY2026', 'Cash effect'],
      ['Trade receivables (48 vs 62 days)', 'R2,177,753', 'R2,813,000', 'R635,247 absorbed'],
      ['Inventory (40 vs 48 days)', 'R1,996,274', 'R2,396,000', 'R399,726 absorbed'],
      ['Trade payables (42 vs 35 days)', 'R2,096,088', 'R1,747,000', 'R349,088 funding lost'],
      ['Total', '', '', 'R1,384,060'],
    ]),
    notes: 'One debtor day is worth R45,370 (16,560,000 ÷ 365); one inventory or creditor day is worth R49,907 (18,216,000 ÷ 365). That is a number sales managers remember. All else equal, the R335,000 overdraft would have been positive cash of about R1,049,000. Accept R1.38m to R1.39m.',
  },
  {
    id: 42, layout: 'comparison', module: 4,
    kicker: 'Lesson 4.4 · Mini case', title: 'Choosing a cash release plan',
    lead: 'Three plans each claim to release about R700,000. Which should the MD back?',
    columns: [
      { heading: 'Plan A: weak', items: ['Pay all suppliers two weeks later from next month', 'Strains suppliers and the import supply chain'] },
      { heading: 'Plan B: best', items: ['Move to KPI targets: debtors 55, inventory 45, creditors 40', 'Collections drive, slow-stock clearance, negotiated terms', 'Named owners and monthly tracking'] },
      { heading: 'Plan C: acceptable', items: ['2% discount for payment within 30 days', 'Releases cash but costs margin'] },
    ],
    notes: 'Ask for a show of hands before revealing the labels. Plan B works on all three levers with owners and targets; at the KPI targets the cycle would be 60 days. Debrief: profit is an opinion about performance; cash is a fact about survival. Keep this short; the capstone goes deeper.',
  },
  {
    id: 43, layout: 'outcome', module: 4,
    kicker: 'Day 1 wrap', title: 'Day 1: what you can now do',
    checks: [
      'Explain what each of the three statements answers',
      'Trace a credit sale through all three statements',
      'Calculate and read the four margins',
      'Calculate the current and quick ratios in context',
      'Explain why Ubuntu made R1.2m profit and ended overdrawn',
      'Value one day of the cash conversion cycle in rand',
    ],
    footnote: 'Exit ticket: one number I now understand, and one I want to challenge.',
    notes: '15 minutes, 16:15 to 16:30. Collect the exit tickets and scan them before Day 2; use the "challenge" answers to shape the morning recap. Remind learners to revisit the Module 1 to 4 lessons and calculators in Okiru Learn tonight.',
  },

  // -------------------------------------------------------------------
  // Day 2
  // -------------------------------------------------------------------
  {
    id: 44, layout: 'agenda',
    kicker: 'Day 2', title: 'Day 2: Use the numbers',
    columns: [
      [
        { time: '09:00', label: 'Recap: Ubuntu\'s FY2026 story in three sentences' },
        { time: '09:15', label: 'Module 5: cost behaviour, break-even, budgets' },
        { time: '10:45', label: 'Tea' },
        { time: '11:00', label: 'Module 5: variance analysis' },
        { time: '11:45', label: 'Module 6: profitability and liquidity ratios' },
        { time: '12:30', label: 'Lunch' },
      ],
      [
        { time: '13:15', label: 'Module 6: efficiency, leverage and returns' },
        { time: '13:45', label: 'Module 7: time value of money, payback' },
        { time: '14:30', label: 'Tea' },
        { time: '14:45', label: 'Module 7: NPV, IRR and the delivery decision' },
        { time: '15:30', label: 'Module 8: budget vs forecast, rolling forecasts' },
        { time: '16:15', label: 'Day 2 wrap; capstone pack issued' },
      ],
    ],
    notes: 'Open with the recap: ask three learners for Ubuntu\'s FY2026 story in three sentences (sales up, profit down, cash gone, and why). Protect the Module 5 variance analysis. The capstone pack goes home tonight for evening reading.',
  },

  // -------------------------------------------------------------------
  // Module 5: Costs, Budgeting and Variance Analysis
  // -------------------------------------------------------------------
  {
    id: 45, layout: 'intro', module: 5,
    kicker: 'Day 2 · Module 05', title: 'Costs, Budgeting and Variance Analysis',
    lead: 'How costs behave, where break-even sits, and how to explain a variance.',
    outcome: 'Classify costs by behaviour, calculate contribution and break-even, build a driver-based budget and explain variances in a way that leads to action.',
    templateAsset: 'problem-solving', imageChrome: 'none',
    notes: '135 minutes: 09:15 to 10:45 for Lessons 5.1 to 5.3, then 11:00 to 11:45 for variance analysis. Time-saver: teach cost behaviour through the Ubuntu opex lines only and set the budget build as homework, but keep the full variance exercise.',
  },
  {
    id: 46, layout: 'lead', module: 5,
    kicker: 'Lesson 5.1 · Cost behaviour', title: 'The seven cost ideas you will use most',
    lead: '',
    extras: table([
      ['Term', 'Plain meaning', 'Example (fictional)'],
      ['Fixed', 'Does not change with activity in the short term', 'Factory rent, salaried supervisors'],
      ['Variable', 'Moves in proportion to activity', 'Imported motors and steel frames per scrubber'],
      ['Semi-variable', 'A fixed base plus a variable part', 'Vehicle lease plus fuel'],
      ['Direct', 'Traced to one product, job or department', 'Components in a pressure washer'],
      ['Indirect', 'Shared and allocated', 'HR, finance, IT support'],
      ['Sunk', 'Already spent, cannot be recovered', 'A feasibility study already paid for'],
      ['Opportunity', 'The benefit given up by the choice', 'Warehouse space full of slow stock'],
    ], 18),
    notes: 'Use Ubuntu examples: motor components are variable and direct; premises rent is fixed and indirect; fleet fuel is semi-variable. Misconception: sunk costs should influence the decision. In the half-finished project case, R180,000 already spent is sunk; compare only R220,000 more to finish against R90,000 for the alternative.',
  },
  {
    id: 47, layout: 'keyterm', module: 5,
    kicker: 'Lesson 5.2 · Formulas', term: 'Contribution and break-even',
    definition: 'Contribution per unit = Selling price − Variable cost per unit. Break-even units = Fixed costs ÷ Contribution per unit.',
    body: 'Break-even revenue = Break-even units × Selling price. Margin of safety = Expected units − Break-even units.',
    callouts: [
      { label: 'Valid range', text: 'Holds within a normal range of activity, where prices and unit costs stay the same.' },
      { label: 'Not a target', text: 'Break-even is the floor below which the business loses money.' },
    ],
    notes: 'Contribution is what each sale adds towards fixed costs and profit. Misconception: break-even is a target. It is the floor. Ask learners which of their decisions move the break-even point: price, variable cost or a new fixed cost.',
  },
  {
    id: 48, layout: 'numbered', module: 5,
    kicker: 'Lesson 5.2 · Worked example', title: 'Break-even for a single product line',
    items: [
      { title: 'Contribution per unit', description: 'R2,000 price − R1,200 variable cost = R800 (40%).' },
      { title: 'Break-even units', description: 'R2,400,000 fixed costs ÷ R800 = 3,000 units.' },
      { title: 'Break-even revenue', description: '3,000 × R2,000 = R6,000,000.' },
      { title: 'Margin of safety at 3,600 units', description: '600 units, or 16.7%. Profit 600 × R800 = R480,000.' },
      { title: 'Price cut to R1,900', description: 'Contribution R700. Break-even rises to 3,429 units.' },
    ],
    notes: 'Sales could fall by one sixth before this line stops covering its fixed costs. Ask: a new R360,000 specialist needs how many extra units? 450. Then the retailer asks for 10% off 400 extra units with spare capacity: contribution R600 a unit, R240,000 in total, worth accepting if the discount does not spread.',
  },
  {
    id: 49, layout: 'lead', module: 5,
    kicker: 'Lesson 5.3 · Building a budget', title: 'Ubuntu FY2027 approved budget',
    lead: '',
    extras: table([
      ['Line (fictional, R)', 'Full-year budget', 'H1 budget (48% phasing)'],
      ['Revenue', 'R30,400,000', 'R14,592,000'],
      ['Cost of sales', '(R19,760,000)', '(R9,485,000)'],
      ['Gross profit', 'R10,640,000', 'R5,107,000'],
      ['Operating expenses (seven lines)', '(R6,780,000)', '(R3,253,000)'],
      ['EBITDA', 'R3,860,000', 'R1,854,000'],
      ['Capex', '(R1,200,000)', '(R576,000)'],
    ]),
    notes: 'A budget is the approved plan, built from drivers: volumes, rates, headcount. Show how H1 is phased at 48% of the full year. Learners build their own with the departmental budget template (the Gqeberha field service example is in Lesson 5.3). Point out that phasing an annual payment evenly causes timing variances; the ERP licence will prove it.',
  },
  {
    id: 50, layout: 'lead', module: 5,
    kicker: 'Lesson 5.4 · H1 FY2027 (1 of 2)', title: 'Actual vs budget: the headline lines',
    lead: '',
    extras: table([
      ['Line (Mar to Aug 2026, R)', 'H1 actual', 'H1 budget', 'Variance', 'F / A'],
      ['Revenue', 'R13,920,000', 'R14,592,000', '−R672,000', 'Adverse'],
      ['Cost of sales', '(R9,410,000)', '(R9,485,000)', 'R75,000', 'Favourable'],
      ['Gross profit', 'R4,510,000', 'R5,107,000', '−R597,000', 'Adverse'],
      ['EBITDA', 'R991,000', 'R1,854,000', '−R863,000', 'Adverse'],
      ['Capex', '(R640,000)', '(R576,000)', '−R64,000', 'Adverse'],
    ]),
    notes: 'Variance = Actual − Budget, labelled by profit effect. Management notes: revenue, two wholesaler orders slipped from August to September; cost of sales, imported components up 9% after rand weakness and a 4% discount to a national retailer. Misconception: cost of sales below budget is favourable. Flexed to actual revenue it is R362,000 adverse.',
  },
  {
    id: 51, layout: 'lead', module: 5,
    kicker: 'Lesson 5.4 · H1 FY2027 (2 of 2)', title: 'Actual vs budget: operating expenses',
    lead: '',
    extras: table([
      ['Line (Mar to Aug 2026, R)', 'H1 actual', 'H1 budget', 'Variance', 'F / A'],
      ['Salaries and wages (non-production)', '(R1,830,000)', '(R1,752,000)', '−R78,000', 'Adverse'],
      ['Premises rent and utilities', '(R520,000)', '(R518,000)', '−R2,000', 'Adverse'],
      ['Fleet and delivery', '(R455,000)', '(R365,000)', '−R90,000', 'Adverse'],
      ['Marketing', '(R95,000)', '(R163,000)', 'R68,000', 'Favourable'],
      ['Technology', '(R210,000)', '(R134,000)', '−R76,000', 'Adverse'],
      ['Insurance', '(R91,000)', '(R91,000)', 'R0', 'On budget'],
      ['Professional fees and administration', '(R318,000)', '(R230,000)', '−R88,000', 'Adverse'],
    ], 18),
    notes: 'Notes from the file: two sales reps hired in April, three months early; fuel increases plus a once-off R38,000 uninsured repair; trade show moved to November; ERP licence paid annually in March but budgeted monthly; a once-off R62,000 legal fee. Misconception: over budget is bad and under budget is good. Marketing is R68,000 under budget only because the trade show moved.',
  },
  {
    id: 52, layout: 'numbered', module: 5,
    kicker: 'Lesson 5.4 · Worked example', title: 'Splitting the gross profit variance',
    items: [
      { title: 'Budget and actual margin', description: 'Budget 35.0% (R5,107,000 ÷ R14,592,000). Actual 32.4%.' },
      { title: 'Flexed gross profit', description: 'Actual revenue at budget margin: R13,920,000 × 35.0% = R4,872,000.' },
      { title: 'Volume effect', description: 'R4,872,000 − R5,107,000 = −R235,000 adverse.' },
      { title: 'Margin effect', description: 'R4,510,000 − R4,872,000 = −R362,000 adverse.' },
      { title: 'Check', description: '−R235,000 + −R362,000 = −R597,000.' },
    ],
    notes: 'This is the key analytical step: always split a gross profit variance into volume and margin. About 40% is volume and may reverse; about 60% is margin and will not fix itself. Learners cannot split the margin effect between component prices and the retailer discount from the data given; a strong answer says so and asks for the discount value.',
  },
  {
    id: 53, layout: 'cards', module: 5,
    kicker: 'Lesson 5.4', title: 'Four kinds of cause, then the consequence',
    lead: 'Label every material variance before deciding on action.',
    columns: 4,
    cards: [
      { title: 'Timing', description: 'Real, but in a different month. Check that it reverses.', tag: 'Trade show, ERP licence' },
      { title: 'Once-off', description: 'Not expected to recur. Watch for repeats.', tag: 'R38,000 repair, R62,000 legal fee' },
      { title: 'Price', description: 'The rate per unit changed.', tag: 'Fuel, components +9%, 4% discount' },
      { title: 'Volume', description: 'The quantity of activity changed.', tag: 'Fewer units sold' },
    ],
    footer: 'Then ask: temporary or structural? Some variances are decisions, such as hiring two reps three months early.',
    notes: 'Four questions for every variance: what changed, why, is it temporary or structural, and what should management do? The same line can hold both: fleet has a once-off repair and a structural fuel increase. A business with a new "once-off" every month has a recurring problem.',
  },
  {
    id: 54, layout: 'exercise', module: 5,
    kicker: 'Activity 5.1', meta: 'Groups · 30 min', title: 'Investigate five variances',
    steps: [
      { title: 'Take your five variances', detail: 'Revenue, gross margin, salaries, fleet and delivery, technology.' },
      { title: 'Answer the four questions', detail: 'What changed? Why? Temporary or structural? What action?' },
      { title: 'Classify each one', detail: 'Timing, once-off, price, volume or decision. Some are a mix.' },
    ],
    checkFor: 'the gross profit variance is split into volume and margin, and every action has an owner.',
    notes: 'Key: revenue is timing (claimed) plus price; cost of sales is price, R362,000 adverse when flexed; salaries are a decision, temporary in budget terms but a permanent cost from July; fleet splits into R38,000 once-off and R52,000 fuel (14.2% over budget excluding the repair); technology is timing from phasing. The adverse EBITDA variance of R863,000 is mostly margin and volume, not overhead (R266,000). Give credit to anyone who questions the revenue timing note; Module 9 returns to it.',
  },

  // -------------------------------------------------------------------
  // Module 6: Ratio Analysis for Managers
  // -------------------------------------------------------------------
  {
    id: 55, layout: 'intro', module: 6,
    kicker: 'Day 2 · Module 06', title: 'Ratio Analysis for Managers',
    lead: 'Five families of ratios, each one a question rather than a score.',
    outcome: 'Calculate the core profitability, liquidity, efficiency, leverage and return ratios and use them to ask the right questions about a business.',
    templateAsset: 'conversation-coaching', imageChrome: 'none',
    notes: '75 minutes: 11:45 to 12:30 for profitability and liquidity (a recap of Modules 2 and 3), then 13:15 to 13:45 for efficiency, leverage and returns. Time-saver: skip the recaps and teach only efficiency, leverage and returns.',
  },
  {
    id: 56, layout: 'keyterm', module: 6,
    kicker: 'Lesson 6.4 · Formulas', term: 'Leverage and returns',
    definition: 'Debt to equity = Total interest-bearing debt (including overdraft) ÷ Total equity.',
    body: 'ROA % = Net profit ÷ Total assets × 100. ROE % = Net profit ÷ Total equity × 100. Closing balances throughout.',
    callouts: [
      { label: 'Asset turnover', text: 'Revenue ÷ Total assets. How hard do the assets work?' },
      { label: 'No magic number', text: 'Levels depend on industry, seasonality, growth stage and business model.' },
    ],
    notes: 'Misconception: there is a correct level for each ratio. Another: debt to equity of 0.83 is low, so debt is fine. Leverage ratios say nothing about whether cash flow can meet repayments. Keep the definitions consistent: debtor days on credit sales, creditor days on cost of sales.',
  },
  {
    id: 57, layout: 'exercise', module: 6,
    kicker: 'Activity 6.1', meta: 'Pairs · 20 min', title: 'Build the Ubuntu ratio table',
    steps: [
      { title: 'Complete the table', detail: 'Profitability, liquidity, efficiency, leverage and returns, FY2025 and FY2026.' },
      { title: 'Find the biggest mover', detail: 'Which ratio moved most, and what operational decision could cause it?' },
      { title: 'Write three questions', detail: 'Each linked to a ratio, each addressed to a named role.' },
    ],
    checkFor: 'at least one question from each family, and no ratio described as simply "good" or "bad".',
    notes: 'Good questions: why did cost of sales grow faster than revenue (gross margin 36.0% to 34.0%)? What changed in collections, customer mix or terms (debtor days 48 to 62)? Why did creditor days shorten from 42 to 35? Ask: if peers run debtor days of 60, is 62 a problem? Yes, relative to Ubuntu\'s own 48 and its target of 55.',
  },
  {
    id: 58, layout: 'lead', module: 6,
    kicker: 'Debrief · Ratios (1 of 2)', title: 'Profitability and liquidity',
    lead: '',
    extras: table([
      ['Ratio', 'FY2025', 'FY2026', 'Direction'],
      ['Gross margin', '36.0%', '34.0%', 'Weaker'],
      ['EBITDA margin', '12.5%', '11.0%', 'Weaker'],
      ['Operating margin', '9.9%', '8.2%', 'Weaker'],
      ['Net margin', '5.7%', '4.3%', 'Weaker'],
      ['Current ratio', '1.58', '1.54', 'Broadly stable'],
      ['Quick ratio', '1.01', '0.83', 'Weaker'],
    ]),
    notes: 'Show after the activity. Every margin weakened, and the biggest single driver is gross margin, so the first questions go to sales and procurement. The current ratio hides the liquidity change that the quick ratio shows.',
  },
  {
    id: 59, layout: 'lead', module: 6,
    kicker: 'Debrief · Ratios (2 of 2)', title: 'Efficiency, leverage and returns',
    lead: '',
    extras: table([
      ['Ratio', 'FY2025', 'FY2026', 'Direction'],
      ['Debtor days', '48', '62', 'Weaker for cash'],
      ['Inventory days', '40', '48', 'Weaker for cash'],
      ['Creditor days', '42', '35', 'Weaker for cash'],
      ['Cash conversion cycle', '46', '75', 'Weaker for cash'],
      ['Asset turnover', '2.37x', '2.35x', 'Broadly stable'],
      ['Debt to equity', '0.78', '0.83', 'Higher'],
      ['ROA', '13.4%', '10.2%', 'Weaker'],
      ['ROE', '31.0%', '23.6%', 'Weaker'],
    ], 19),
    notes: 'Asset turnover is broadly stable, so the fall in ROA is driven by the net margin fall, not by assets working less hard. Read families together: margin times asset turnover explains ROA. Misconception: ROE of 23.6% is excellent. It is high partly because the business uses debt, and it fell sharply from 31.0%.',
  },
  {
    id: 60, layout: 'comparison', module: 6,
    kicker: 'Lesson 6.4 · Mini case', title: 'Reading the whole picture',
    lead: 'A board member: "Revenue up 15%, ROE above 20%, debt to equity under 1. Approve the second vehicle and a higher dividend."',
    columns: [
      { heading: 'Weak responses', items: ['Agree: ROE above 20% means the business can afford both', 'Disagree: 0.83 debt to equity is dangerously high, so approve nothing'] },
      { heading: 'Balanced response', items: ['Every margin and return fell', 'The cycle lengthened by 29 days', 'The year ended in overdraft', 'Fix working capital and confirm FY2027 cash first'] },
    ],
    notes: 'Ask the room to argue both sides before revealing. Ratios point to questions; operations provide the answers. A single-year ratio means little; the trend and the business\'s own targets matter. Optional extension for a confident group: ROE = net margin × asset turnover × assets ÷ equity (2.31 in both years), so the fall is margin-driven.',
  },

  // -------------------------------------------------------------------
  // Module 7: Investment Decisions
  // -------------------------------------------------------------------
  {
    id: 61, layout: 'intro', module: 7,
    kicker: 'Day 2 · Module 07', title: 'Investment Decisions',
    lead: 'A rand today is worth more than a rand later. Judge investments on discounted cash flows.',
    outcome: 'Evaluate a simple investment using time value of money, payback, NPV and IRR, and combine the numbers with operational risk to make a recommendation.',
    templateAsset: 'thinking-learner', imageChrome: 'none',
    notes: '90 minutes: 13:45 to 14:30 for time value and payback, 14:45 to 15:30 for NPV, IRR and the delivery decision. Every rate in this module is a training assumption; never quote a live market rate in the room.',
  },
  {
    id: 62, layout: 'keyterm', module: 7,
    kicker: 'Lesson 7.1 · Time value of money', term: 'Present value',
    definition: 'Present value = Future cash flow ÷ (1 + r)^n. The factor 1 ÷ (1 + r)^n is the discount factor.',
    body: 'At the 12% training rate, R100,000 received in year 1 is worth R89,286 today; in year 5, R56,743.',
    callouts: [
      { label: 'Discount factors at 12%', text: 'Year 1: 0.892857. Year 2: 0.797194. Year 3: 0.711780. Year 4: 0.635518. Year 5: 0.567427.' },
      { label: 'Pay now or later?', text: 'R1,100,000 in a year is worth R982,143 today, so paying later costs R17,857 less.' },
    ],
    notes: 'Ask "R100,000 today or R100,000 in three years?" before any formula. The supplier in the example effectively charges 10% for a year of credit while Ubuntu values money at 12%, so deferring is better. If Ubuntu\'s own cost of money were below 10%, paying today could win: the decision depends on the rate.',
  },
  {
    id: 63, layout: 'numbered', module: 7,
    kicker: 'Lesson 7.2 · Worked example', title: 'Payback on a R600,000 packaging machine',
    items: [
      { title: 'Savings', description: 'R180,000, R220,000, R260,000, R260,000, R200,000 in years 1 to 5 (fictional).' },
      { title: 'After year 2', description: 'R400,000 recovered; R200,000 still to recover.' },
      { title: 'Fraction of year 3', description: 'R200,000 ÷ R260,000 = 0.77 years.' },
      { title: 'Payback', description: '2 + 0.77 = 2.77 years, about 2.8 years.' },
      { title: 'What payback misses', description: 'The R460,000 saved in years 4 and 5, and the time value of money.' },
    ],
    notes: 'Payback is simple and liquidity focused: how long is our cash at risk? Misconception: the shortest payback wins. Payback ignores cash after the payback point and the size of the value created. Quick check: Project Y (R500,000; R100,000 to R300,000 a year) pays back in 3.2 years.',
  },
  {
    id: 64, layout: 'lead', module: 7,
    kicker: 'Lesson 7.3 · Net present value', title: 'Packaging machine NPV at 12%',
    lead: '',
    extras: table([
      ['Year', 'Cash saving', 'Discount factor (12%)', 'Present value'],
      ['1', 'R180,000', '0.892857', 'R160,714'],
      ['2', 'R220,000', '0.797194', 'R175,383'],
      ['3', 'R260,000', '0.711780', 'R185,063'],
      ['4', 'R260,000', '0.635518', 'R165,235'],
      ['5', 'R200,000', '0.567427', 'R113,485'],
      ['Total', 'R1,120,000', '', 'R799,880'],
      ['NPV after the R600,000 investment', '', '', 'R199,880'],
    ]),
    notes: 'NPV = present value of future cash inflows − initial investment. Undiscounted savings of R1,120,000 are worth R799,880 today, so the machine creates about R200,000 of value above the 12% training rate. Misconceptions: a positive NPV means approve (test the assumptions first), and depreciation is a cash flow in the NPV (it is not; use cash flows).',
  },
  {
    id: 65, layout: 'lead', module: 7,
    kicker: 'Lesson 7.4 · IRR', title: 'Payback, NPV and IRR: what each tells you',
    lead: '',
    extras: table([
      ['Measure', 'Question it answers', 'Strength', 'Main limitation'],
      ['Payback', 'How quickly do we recover the cash?', 'Simple; highlights cash risk', 'Ignores later cash flows and time value'],
      ['NPV', 'How much value does it add in today\'s rand?', 'Uses all cash flows and time value', 'Depends on the chosen discount rate'],
      ['IRR', 'What annual return does it earn?', 'Intuitive percentage', 'Ignores scale; can mislead'],
    ], 19),
    notes: 'IRR is the rate at which NPV is zero. Misconception: higher IRR is always better. Proposal A (R200,000, NPV R90,000, IRR 35%) against Proposal B (R800,000, NPV R210,000, IRR 22%): B adds more value. NPV answers "does it create value?"; payback answers "how long is our cash at risk?". Use both.',
  },
  {
    id: 66, layout: 'lead', module: 7,
    kicker: 'Module 7 activity · The data', title: 'Delivery vehicle or outsourced logistics?',
    lead: '',
    extras: table([
      ['Year', 'Option A running cost', 'Option A net saving', 'Option B contract cost', 'Option B net saving'],
      ['0', 'Investment R850,000', '(R850,000)', 'Onboarding R60,000', '(R60,000)'],
      ['1', 'R350,000', 'R290,000', 'R570,000', 'R70,000'],
      ['2', 'R350,000', 'R290,000', 'R570,000', 'R70,000'],
      ['3', 'R370,000', 'R270,000', 'R570,000', 'R70,000'],
      ['4', 'R390,000', 'R250,000', 'R570,000', 'R70,000'],
      ['5', 'R410,000', 'R230,000 + R170,000 trade-in', 'R570,000', 'R70,000'],
    ], 18),
    notes: 'Savings are against Ubuntu\'s current courier baseline (fictional figures from the Module 7 activity). Option A buys a vehicle; Option B is an outsourced contract with exit possible after year 2. Set the activity next and keep the results off the screen until groups have calculated.',
  },
  {
    id: 67, layout: 'exercise', module: 7,
    kicker: 'Activity 7.1', meta: 'Groups · 25 min', title: 'Make the delivery decision',
    steps: [
      { title: 'Calculate payback and NPV', detail: 'Both options at the 12% training rate.' },
      { title: 'Stress the key assumption', detail: 'What if the vehicle has no trade-in value?' },
      { title: 'Weigh cash and risk', detail: 'The overdraft is above its threshold. Recommend one option with conditions.' },
    ],
    checkFor: 'the recommendation uses NPV and payback together, names the assumption that drives the result and links the choice to the cash position.',
    notes: 'Option A: payback 3.0 years, NPV R218,146, IRR about 21.6%. Option B: payback 0.86 years, NPV R192,334 (R70,000 × 3.604776 − R60,000). With no trade-in, Option A falls to R121,683. Best-supported answer: Option B now, with service-level KPIs, and review buying in FY2028. Weak: "A because NPV is higher" and "B because IRR is over 100%" (right answer, wrong reason).',
  },
  {
    id: 68, layout: 'lead', module: 7,
    kicker: 'Debrief · Module 7 activity', title: 'Summary of the comparison',
    lead: '',
    extras: table([
      ['Factor', 'Option A: buy vehicle', 'Option B: outsource'],
      ['Cash needed now', 'R850,000', 'R60,000'],
      ['Payback', '3.0 years', '0.86 years'],
      ['NPV at 12% (training rate)', 'About R218,000', 'About R192,000'],
      ['IRR', 'About 21.6%', 'Over 100% (small investment)'],
      ['NPV if no trade-in value', 'About R122,000', 'Not affected'],
      ['Control of service', 'High: own driver and vehicle', 'Depends on service levels'],
      ['Flexibility', 'Low: resale needed to exit', 'Exit on notice after year 2'],
      ['Key risks', 'Breakdowns, driver absence, fuel', 'Provider performance, renewal price'],
    ], 18),
    notes: 'Show after the activity. The NPVs are close and Option A\'s lead depends entirely on a resale value five years away. The cash position of the business is part of the investment decision. Optional extension: Option A needs a trade-in of at least about R124,500 to stay ahead.',
  },

  // -------------------------------------------------------------------
  // Module 8: Planning and Forecasting
  // -------------------------------------------------------------------
  {
    id: 69, layout: 'intro', module: 8,
    kicker: 'Day 2 and Day 3 · Module 08', title: 'Planning and Forecasting',
    lead: 'The budget is the promise. The forecast is the latest honest view.',
    outcome: 'Distinguish a budget from a forecast, update a rolling forecast, and use scenarios and sensitivities to show what could happen and what to do.',
    templateAsset: 'take-action', imageChrome: 'none',
    notes: '105 minutes: 15:30 to 16:15 on Day 2 for budget vs forecast and rolling forecasts, then 09:15 to 10:15 on Day 3 for scenarios and sensitivity. Time-saver: run two sensitivities (price and debtor days) instead of five.',
  },
  {
    id: 70, layout: 'comparison', module: 8,
    kicker: 'Lesson 8.1', title: 'Budget vs forecast: two documents, two jobs',
    columns: [
      { heading: 'Budget', items: ['The coming year, as planned', 'Approved once a year', 'Allocating resources and accountability', 'Stays fixed as the benchmark'] },
      { heading: 'Rolling forecast', items: ['Actuals to date plus expected remainder', 'Updated monthly or quarterly', 'Decisions on cash, costs, capex and funding', 'Moves with the evidence'] },
    ],
    notes: 'Misconception: updating the forecast means changing the budget. The budget stays; the forecast shows the gap. Mini case: a sales director wants H2 put at R16.48m to hit budget with no new pipeline evidence. Keep the forecast at R15.48m and record the extra R1.0m as a stretch target until named deals are secured.',
  },
  {
    id: 71, layout: 'lead', module: 8,
    kicker: 'Lesson 8.1 · Ubuntu FY2027', title: 'Rolling forecast against the budget',
    lead: '',
    extras: table([
      ['Line (fictional, R)', 'H1 actual', 'H2 forecast', 'Full year', 'Budget'],
      ['Revenue', 'R13,920,000', 'R15,480,000', 'R29,400,000', 'R30,400,000'],
      ['Gross margin', '32.4%', '33.0%', '32.7%', '35.0%'],
      ['Gross profit', 'R4,510,000', 'R5,108,400', 'R9,618,400', 'R10,640,000'],
      ['Payroll (non-production)', 'R1,830,000', 'R1,900,000', 'R3,730,000', 'R3,650,000'],
      ['Other operating expenses', 'R1,689,000', 'R1,640,000', 'R3,329,000', 'R3,130,000'],
      ['EBITDA', 'R991,000', 'R1,568,400', 'R2,559,400', 'R3,860,000'],
      ['Capex', 'R640,000', 'R560,000', 'R1,200,000', 'R1,200,000'],
    ], 18),
    notes: 'The forecast lands about R1.3m (33.7%) below the EBITDA budget on revenue only 3.3% below budget, and below FY2026\'s R3,034,000. Most of the gap is margin. Ask: does H2 depend on the slipped orders arriving? H2 revenue includes R1,100,000 of them. And the 33% H2 margin relies on a 3% price increase from 1 October: what if customers resist?',
  },
  {
    id: 72, layout: 'outcome', module: 8,
    kicker: 'Day 2 wrap', title: 'Day 2: what you can now do',
    checks: [
      'Classify costs and calculate contribution and break-even',
      'Split a variance into volume and margin, and label its cause',
      'Build and read the five ratio families as questions',
      'Judge an investment on payback and NPV, with its risks',
      'Explain the difference between a budget and a forecast',
    ],
    footnote: 'Tonight: read the capstone pack. Bring your questions to the Day 3 recap.',
    notes: '15 minutes, 16:15 to 16:30. Issue the capstone pack for evening reading: the two-year statements, FY2027 budget, H1 actuals with notes, rolling forecast and KPI pack. Remind learners that the Module 8 lessons on scenarios and sensitivity open tomorrow.',
  },

  // -------------------------------------------------------------------
  // Day 3
  // -------------------------------------------------------------------
  {
    id: 73, layout: 'agenda',
    kicker: 'Day 3', title: 'Day 3: Plan and run the business',
    columns: [
      [
        { time: '09:00', label: 'Recap and capstone questions' },
        { time: '09:15', label: 'Module 8: scenarios and sensitivity analysis' },
        { time: '10:15', label: 'Module 9: the monthly pack and commentary' },
        { time: '10:45', label: 'Tea' },
        { time: '11:00', label: 'Module 9: KPI design' },
        { time: '11:30', label: 'Module 10: risk, controls and governance' },
      ],
      [
        { time: '12:30', label: 'Lunch' },
        { time: '13:15', label: 'Capstone: team analysis' },
        { time: '14:30', label: 'Tea' },
        { time: '14:45', label: 'Capstone: finish, then 5-minute summaries' },
        { time: '16:15', label: 'Close: certification, submission, evaluation' },
      ],
    ],
    notes: 'Take capstone questions in the recap but do not give answers away. If running late, compress Module 10 before the capstone; protect the capstone blocks.',
  },
  {
    id: 74, layout: 'lead', module: 8,
    kicker: 'Lesson 8.3 · Scenario planning', title: 'Ubuntu H2 FY2027 scenarios',
    lead: '',
    extras: table([
      ['Measure (fictional, R)', 'Downside (revenue −15%)', 'Base (latest forecast)', 'Upside (revenue +5%)'],
      ['H2 revenue', 'R13,158,000', 'R15,480,000', 'R16,254,000'],
      ['H2 gross profit (33.0%)', 'R4,342,140', 'R5,108,400', 'R5,363,820'],
      ['H2 operating expenses', 'R3,540,000', 'R3,540,000', 'R3,540,000'],
      ['H2 EBITDA', 'R802,140', 'R1,568,400', 'R1,823,820'],
      ['Full-year EBITDA', 'R1,793,140', 'R2,559,400', 'R2,814,820'],
      ['Against budget (R3,860,000)', '−R2,066,860', '−R1,300,600', '−R1,045,180'],
    ], 19),
    notes: 'Revenue falls 15% but H2 EBITDA falls 48.9%, because operating expenses are largely fixed in the short term. Misconception: revenue down 15% means profit down 15%. Another: a downside scenario is pessimism. It is preparation: agree the levers and the trigger for each (pause discretionary spend, freeze hiring, cut stock purchasing, collections drive, defer capex).',
  },
  {
    id: 75, layout: 'lead', module: 8,
    kicker: 'Lesson 8.4 · Sensitivity analysis', title: 'One variable at a time, H2 FY2027',
    lead: '',
    extras: table([
      ['Variable changed', 'Change', 'Effect on H2 EBITDA', 'H2 EBITDA after', 'Effect on cash'],
      ['None (base)', '', '', 'R1,568,400', ''],
      ['Selling price', '−5%', '−R774,000 (−49.3%)', 'R794,400', 'Lower, via profit'],
      ['Raw material cost', '+5%', '−R363,006 (−23.1%)', 'R1,205,394', 'Lower, via profit'],
      ['Exchange rate', 'Rand 10% weaker', '−R259,290 (−16.5%)', 'R1,309,110', 'Lower, via profit'],
      ['Volume', '−5%', '−R255,420 (−16.3%)', 'R1,312,980', 'Partly offset by lower receivables'],
      ['Debtor days', '+10 days (60 to 70)', 'None directly', 'R1,568,400', 'About R509,000 more tied up'],
    ], 17),
    notes: 'Raw materials at 70% and imports at 25% of cost of sales are training assumptions from Lesson 8.4. A 5% price cut hurts three times as much as a 5% volume fall, because the full price reduction drops to profit. Sensitivity moves one variable; a scenario moves several consistently. Price and debtor days are the two to watch: the biggest profit effect and the biggest cash effect.',
  },
  {
    id: 76, layout: 'exercise', module: 8,
    kicker: 'Activity 8.1', meta: 'Groups · 25 min', title: 'Stress the forecast',
    steps: [
      { title: 'Run the downside', detail: 'H2 revenue 15% below base, margin and opex unchanged. What happens to EBITDA?' },
      { title: 'Rank the sensitivities', detail: 'Volume, price, raw material cost, debtor days, exchange rate.' },
      { title: 'Set triggers', detail: 'For your top two, name the action, the owner and the trigger point.' },
    ],
    checkFor: 'each scenario is presented with the action and the trigger, and assumptions are stated.',
    notes: 'Debrief: price is the most powerful lever per percentage point, which is why the 3% October increase matters so much to H2. A forecast is only as good as the evidence behind each assumption. The exchange rate effect depends on the imported share of cost of sales, which Dataset A does not give; Lesson 8.4 uses 25% as a training assumption.',
  },

  // -------------------------------------------------------------------
  // Module 9: Management Accounts and KPIs
  // -------------------------------------------------------------------
  {
    id: 77, layout: 'intro', module: 9,
    kicker: 'Day 3 · Module 09', title: 'Management Accounts and KPIs',
    lead: 'A pack is only useful if reading it ends in a named action, an owner and a date.',
    outcome: 'Read a monthly management accounts pack in a structured way, challenge commentary with evidence-based questions, and design KPIs that trigger timely action.',
    templateAsset: 'action-communication', imageChrome: 'none',
    notes: '60 minutes: 10:15 to 10:45 for the pack and commentary, 11:00 to 11:30 for KPI design. Time-saver: give the pack contents as a handout and run only the commentary challenge and the KPI breach table.',
  },
  {
    id: 78, layout: 'comparison', module: 9,
    kicker: 'Lesson 9.1', title: 'The ten parts of a useful monthly pack',
    columns: [
      { heading: 'The numbers', items: ['1. Executive summary', '2. Income Statement', '3. Balance Sheet', '4. Cash flow', '5. Budget vs actual'] },
      { heading: 'Looking ahead and acting', items: ['6. Rolling forecast', '7. Working capital', '8. KPI dashboard', '9. Commentary', '10. Action items'] },
    ],
    callout: { label: 'A fifteen-minute routine', text: 'Summary and actions, P&L against budget, cash and balance sheet, KPIs, then your own lines. Leave with three questions and one action.' },
    notes: 'Ask which parts learners actually receive today. Walk the fifteen-minute routine: for Ubuntu\'s H1 you immediately see revenue 4.6% below budget, gross margin 32.4% against 35.0% and EBITDA 46.5% below budget. The gap between the revenue and EBITDA variances says margin and overheads matter more than volume.',
  },
  {
    id: 79, layout: 'numbered', module: 9,
    kicker: 'Lesson 9.2', title: 'Five questions for any commentary',
    items: [
      { title: 'What evidence?', description: 'Orders, invoices, supplier notices. With none, it is an opinion.' },
      { title: 'How much?', description: 'The rand value. If it covers only part of the variance, what explains the rest?' },
      { title: 'When?', description: 'When did it start, and in which month will it reverse?' },
      { title: 'What action?', description: 'What is management doing? If nothing, why not?' },
      { title: 'Who owns it?', description: 'A named person who reports back next month.' },
    ],
    notes: 'Test the three phrases from the lesson: "below budget due to market conditions", "margin pressure was temporary", "cash flow will normalise next month". Misconception: commentary explains the numbers. Commentary is a claim; it needs evidence, quantification and an owner.',
  },
  {
    id: 80, layout: 'comparison', module: 9,
    kicker: 'Lesson 9.2 · Worked example', title: 'Rewriting Ubuntu\'s revenue commentary',
    columns: [
      { heading: 'Weak', items: ['"Revenue was below budget for H1 due to market conditions."'] },
      { heading: 'Stronger', items: ['H1 revenue R13.92m, R672,000 (4.6%) below budget', 'Two wholesaler orders worth R1.1m moved to September', 'Excluding them, H1 would be R428,000 above budget', 'Sales Director confirms September invoicing'] },
    ],
    notes: 'The stronger version replaces a vague external cause with a fact that can be verified next month, and notice the weak one hid the bigger issue: margin. Facilitator only: the KPI pack shows the revenue shortfall against the R2,430,000 monthly target concentrated in March to June, with August only R10,000 short, and the R1.1m claimed slippage exceeds the whole H1 variance. Model questions: which orders, for how much, invoiced when, and why is August almost on target?',
  },
  {
    id: 81, layout: 'keyterm', module: 9,
    kicker: 'Lesson 9.3 · Key term', term: 'A useful KPI',
    definition: 'A measure chosen because it drives or signals performance, with seven attributes.',
    body: 'Definition, formula, target, actual, trend, owner and action threshold.',
    callouts: [
      { label: 'Target vs threshold', text: 'The target is where you want to be. The threshold is where "watch and wait" stops.' },
      { label: 'Leading and lagging', text: 'Mix measures that move early (on-time delivery) with ones that confirm results (gross margin).' },
    ],
    notes: 'Misconception: more KPIs means better control. Too many dilute attention; every KPI needs an owner and a threshold that triggers action. A threshold without an action is decoration. Another: green on the dashboard means fine. A KPI can be within threshold and still trending the wrong way.',
  },
  {
    id: 82, layout: 'lead', module: 9,
    kicker: 'Lesson 9.3 · Ubuntu KPI pack', title: 'Monthly KPIs, March to August 2026',
    lead: '',
    extras: table([
      ['KPI (fictional)', 'Mar to Aug', 'Target', 'Action threshold'],
      ['Revenue (R000)', '2,180 · 2,240 · 2,310 · 2,350 · 2,420 · 2,420', '2,430', 'Below R2.2m for two consecutive months'],
      ['Gross margin %', '33.4 · 33.1 · 32.6 · 32.2 · 32.4 · 32.9', '35', 'Below 33%'],
      ['Debtor days', '63 · 64 · 65 · 67 · 68 · 66', '55', 'Above 60 days'],
      ['Inventory days', '49 · 50 · 51 · 53 · 54 · 52', '45', 'Above 50 days'],
      ['Creditor days', '35 · 34 · 34 · 33 · 34 · 34', '40', 'Below 35 days'],
      ['On-time delivery %', '94 · 92 · 91 · 89 · 90 · 93', '95', 'Below 92%'],
      ['Overdraft (R000, month-end)', '180 · 420 · 610 · 780 · 690 · 540', '0', 'Above R500,000'],
      ['Headcount (non-production)', '30 · 32 · 32 · 32 · 32 · 32', '30', 'Any unbudgeted hire'],
    ], 17),
    notes: 'Monthly figures are flash reports before half-year stock-count adjustments; the adjusted H1 gross margin is 32.4%. A learner who spots the gap has read the pack well. Ask: which KPI breached its threshold in every month? Debtor days. Who owns overdraft utilisation, and what did they do in May when it passed R500,000?',
  },
  {
    id: 83, layout: 'exercise', module: 9,
    kicker: 'Activity 9.1', meta: 'Pairs · 20 min', title: 'Read the KPI pack',
    steps: [
      { title: 'Mark every breach', detail: 'Which KPIs passed their action threshold, and in which months?' },
      { title: 'Challenge one commentary line', detail: 'Use the five questions on the revenue or margin note.' },
      { title: 'Design one KPI card', detail: 'For your own area, with all seven attributes and the action on breach.' },
    ],
    checkFor: 'breaches are counted against the threshold, not the target, and every KPI card names an owner and an action.',
    notes: 'Breaches: gross margin May to August; debtor days all six months (peak 68 in July); inventory days May to August; creditor days April to August (March is exactly 35, not below); on-time delivery May to July; overdraft May to August (peak R780,000 in June); headcount April (30 to 32). Revenue: none, because March was below R2.2m but April was not. Debrief: debtor days, inventory days, creditor days and overdraft all breached together; that is the cash story in real time.',
  },

  // -------------------------------------------------------------------
  // Module 10: Financial Risk, Controls and Governance
  // -------------------------------------------------------------------
  {
    id: 84, layout: 'intro', module: 10,
    kicker: 'Day 3 · Module 10', title: 'Financial Risk, Controls and Governance',
    lead: 'Most losses come from ordinary processes with a missing check.',
    outcome: 'Identify the main financial risks in your area, recognise controls that prevent, detect and correct them, and recommend practical improvements.',
    templateAsset: 'icon-professionalism', imageChrome: 'none',
    notes: '60 minutes, 11:30 to 12:30. Managers own the controls in their own processes: approvals, stock counts, timesheets. Time-saver: teach categories and controls as one 10-minute segment and run the activity with three risks.',
  },
  {
    id: 85, layout: 'cards', module: 10,
    kicker: 'Lesson 10.1', title: 'Ten financial risk categories',
    columns: 5,
    cards: [
      { title: 'Liquidity', description: 'Not enough cash when due.' },
      { title: 'Credit', description: 'Customers pay late or not at all.' },
      { title: 'Fraud', description: 'Deliberate deception for gain.' },
      { title: 'Pricing', description: 'Prices or discounts below cost.' },
      { title: 'Procurement', description: 'Wrong price, item or supplier.' },
      { title: 'Payroll', description: 'Paying for work not done.' },
      { title: 'Inventory', description: 'Stock lost, damaged or obsolete.' },
      { title: 'Foreign exchange', description: 'The rand moves against costs.' },
      { title: 'Compliance', description: 'Tax, labour, data or company law.' },
      { title: 'Cyber and payment fraud', description: 'Payments intercepted or redirected.' },
    ],
    notes: 'Rate each risk on likelihood and impact, based on evidence. At Ubuntu the evidence already points at credit and liquidity: debtor days above threshold for six months and the overdraft above R500,000 for four. Imported components up 9% show foreign exchange risk is not theoretical. Misconception: payment fraud is an IT problem. The key protection is a process.',
  },
  {
    id: 86, layout: 'lead', module: 10,
    kicker: 'Lesson 10.2 · Prevent, detect, correct', title: 'Eight core controls',
    lead: '',
    extras: table([
      ['Control', 'Type', 'Example in a manager\'s area'],
      ['Segregation of duties', 'Preventive', 'Who orders stock does not receive it or approve the invoice'],
      ['Approvals', 'Preventive', 'Overtime approved against job cards before payment'],
      ['Reconciliations', 'Detective', 'Stock system against physical count'],
      ['Access control', 'Preventive', 'Named log-ins; banking tokens held only by signatories'],
      ['Exception reporting', 'Detective', 'Payments to newly changed bank accounts'],
      ['Supplier verification', 'Preventive', 'Registration, tax and bank checks before loading'],
      ['Bank-detail verification', 'Preventive', 'Call-back on a number already on file'],
      ['Budget authority limits', 'Preventive', 'Spend approved only up to a defined limit'],
    ], 18),
    notes: 'Corrective controls fix the problem and stop it recurring: recovering an overpayment, updating a procedure after an incident. A good control set has some of each type. Misconception: more approvals means more control. Rubber-stamped approvals add delay without protection. Controls protect honest staff as much as the business.',
  },
  {
    id: 87, layout: 'numbered', module: 10,
    kicker: 'Lesson 10.2', title: 'A bank-detail change procedure that works',
    items: [
      { title: 'Treat every request as unverified', description: 'Email, letter, phone or in person. Do not act on attached documents.' },
      { title: 'Verify independently', description: 'Call a number already on the supplier master file, never the one in the request.' },
      { title: 'Confirm the account', description: 'Bank confirmation, and account verification where the bank offers it.' },
      { title: 'Second-person approval', description: 'Someone other than the capturer approves, with evidence attached.' },
      { title: 'Notify and monitor', description: 'Confirm to the contact on file; review payments to changed accounts.' },
    ],
    notes: 'Ask: what would stop a fake "change of bank details" email from being paid in your area? Mini case: a Friday 16:30 email "from the MD" asks for an urgent R185,000 payment to a new supplier. Do not pay; urgency and unreachability are warning signs, not reasons to skip controls.',
  },
  {
    id: 88, layout: 'exercise', module: 10,
    kicker: 'Module 10 activity', meta: 'Groups · 30 min', title: 'Review the service and spares depot',
    steps: [
      { title: 'List five risks', detail: 'Name each risk and its category. Tie it to a fact in the case.' },
      { title: 'Record control and gap', detail: 'What exists today, whether it operates, and what is missing.' },
      { title: 'Rate and recommend', detail: 'Likelihood and impact, then a proportionate action with an owner and date.' },
    ],
    checkFor: 'the suspected redirected payment is treated as an incident first, and compensating controls are used where the team is too small to split duties.',
    notes: 'The depot case is fictional and in the Module 10 activity lesson. Strong reviews catch: the unverified bank-detail change and the unpaid supplier (inform the financial manager and bank now, freeze further changes); the banking token in an unlocked drawer and the shared payments password; stock write-offs with no approval and a count by the clerk alone; no second-person cash-up; overtime signed without job cards; fuel slips not matched to the logbook. Credit what works: head office bank reconciliation, the R20,000 purchase order rule, CCTV, named stock log-ins.',
  },

  // -------------------------------------------------------------------
  // Capstone: Run the Business Review
  // -------------------------------------------------------------------
  {
    id: 89, layout: 'intro', module: 11,
    kicker: 'Day 3 · Capstone', title: 'Run the Business Review',
    lead: 'It is mid-September 2026. The MD wants an honest diagnosis, a clear view of cash and a capex recommendation.',
    outcome: 'Run a complete financial review of Ubuntu: ten metrics, five issues and causes, actions, cash, a capex proposal, a forecast update and a five-minute summary.',
    templateAsset: 'team-collaboration', imageChrome: 'none',
    notes: '165 minutes: team analysis 13:15 to 14:30, then finish the analysis (45 minutes) and 5-minute management summaries with feedback (45 minutes). The individual written Manager Financial Review is the marked submission, submitted after the course by the deadline set for the cohort.',
  },
  {
    id: 90, layout: 'cards', module: 11,
    kicker: 'Lesson 11.1', title: 'The eight required tasks',
    columns: 4,
    cards: [
      { title: '1. Ten key metrics', description: 'FY2026 against FY2025.' },
      { title: '2. Five issues', description: 'Each backed by at least one metric.' },
      { title: '3. Causes', description: 'Fact separated from judgement.' },
      { title: '4. Three actions', description: 'Specific, owned and dated.' },
      { title: '5. Cash implications', description: 'Tied up, released, and the overdraft.' },
      { title: '6. One capex proposal', description: 'Payback, NPV at 12%, a decision.' },
      { title: '7. Forecast update', description: 'For the September news.' },
      { title: '8. Five-minute summary', description: 'The opening for the executive meeting.' },
    ],
    notes: 'The data room: two-year statements, the FY2027 budget, H1 actuals with notes, the rolling forecast and the KPI pack. Accuracy matters, but most marks sit in interpretation, judgement and recommendations. Reward learners who tie every claim to a number, separate fact from judgement and give each action an owner, a size and a date. Do not reward length.',
  },
  {
    id: 91, layout: 'lead', module: 11,
    kicker: 'Lesson 11.4 · Task 6 inputs', title: 'The capex proposal: a barcode stock system',
    lead: '',
    extras: table([
      ['Item (capstone assumption)', 'Proposal as submitted'],
      ['Investment, paid up front', 'R480,000'],
      ['Annual cash savings', 'R150,000 a year for five years'],
      ['Inventory release', 'Inventory days 48 to 43: about R250,000 in year 1'],
      ['Year 1 cash inflow', 'R400,000 (R150,000 + R250,000)'],
      ['Years 2 to 5', 'R150,000 each'],
      ['Discount rate', '12% (training assumption)'],
    ]),
    notes: 'Answers for facilitators: as submitted, payback 1.53 years and NPV +R283,931; without the inventory release, NPV +R60,716 and payback 3.2 years. Sharper test: the forecast already assumes 46 days, so credit only 3 days (R149,721): NPV +R194,396, payback 2.20 years; give extra credit for this. Best-supported: approve with conditions (owned inventory target, savings tracked in the KPI pack, payment timed so the overdraft is not pushed higher).',
  },
  {
    id: 92, layout: 'numbered', module: 11,
    kicker: 'Lesson 11.4 · Task 7 inputs', title: 'The September update',
    items: [
      { title: 'One order delivered', description: 'R600,000 slipped order delivered and invoiced by 30 September.' },
      { title: 'One order cancelled', description: 'R500,000 order lost to a lower-priced competitor product.' },
      { title: 'Everything else unchanged', description: 'H2 margin 33.0%, payroll R1,900,000, other opex R1,640,000, capex R560,000.' },
      { title: 'Think beyond the arithmetic', description: 'Part of a "timing" variance is now a lost sale. What else should change?' },
    ],
    notes: 'Expected answer: revised H2 revenue R14,980,000 and full year R28,900,000 (R1,500,000 or 4.9% below budget). H2 gross profit R4,943,400, H2 EBITDA R1,403,400, full-year EBITDA R2,394,400: down R165,000 on the previous forecast and R1,465,600 below budget. The best answers link the lost sale to price competition and the margin issue, as a judgement to test. Adding H1 actual to H2 budget is not a forecast update.',
  },
  {
    id: 93, layout: 'exercise', module: 11,
    kicker: 'Capstone · Block 1', meta: 'Teams · 75 min', title: 'Team analysis',
    steps: [
      { title: 'Tasks 1 to 3', detail: 'Calculate the ten metrics, name five issues, explain their causes.' },
      { title: 'Tasks 4 and 5', detail: 'Choose three actions and size the cash they absorb or release.' },
      { title: 'Tasks 6 and 7', detail: 'Evaluate the capex proposal and update the FY2027 forecast.' },
    ],
    checkFor: 'every issue carries a number, every cause is marked as fact or judgement, and every action has an owner, a size and a date.',
    notes: 'Five issues to listen for: margins eroded while revenue grew; collections slowed (48 to 62 debtor days); inventory built up (40 to 48 days); supplier terms shortened (42 to 35 days); cash fell to a R335,000 overdraft despite R1.2m profit. Returning to FY2025 days frees about R1.38m. An invented cause stated as fact loses judgement marks; the same point stated as a hypothesis with the evidence needed earns them. Weak actions: cut all costs 10%, stop all capex, delay suppliers beyond terms, grow sales faster, increase the overdraft.',
  },
  {
    id: 94, layout: 'numbered', module: 11,
    kicker: 'Lesson 11.5 · Task 8', title: 'Structuring a five-minute summary',
    items: [
      { title: 'The headline (30 seconds)', description: 'The position and what you recommend.' },
      { title: 'What is happening (90 seconds)', description: 'Three or four numbers, each with its comparison.' },
      { title: 'Why (60 seconds)', description: 'Causes and evidence, fact separated from judgement.' },
      { title: 'What it means for cash (45 seconds)', description: 'Tied up, releasable, and the overdraft.' },
      { title: 'What we should do (75 seconds)', description: 'Three actions, the capex call and the forecast, with owners.' },
      { title: 'The ask (15 seconds)', description: 'The decision you need today.' },
    ],
    notes: 'Five minutes is about 600 to 700 spoken words. Say "customers take 62 days to pay, up from 48" rather than "debtor days deteriorated", and round sensibly: R1.38m, not every digit. Model opening for calibration only, do not share: sales up 15%, profit down 12%, from R1.08m in the bank to a R335,000 overdraft, and about R1.4m free if collection, stock and supplier days return to last year.',
  },
  {
    id: 95, layout: 'lead', module: 11,
    kicker: 'Lesson 11.5 · Rubric', title: 'How the Manager Financial Review is judged',
    lead: '',
    extras: table([
      ['Criterion', 'Weight', 'What strong work shows'],
      ['Accuracy of calculations', '25%', 'Metrics, cash estimates, capex and forecast correct and consistently rounded'],
      ['Interpretation', '25%', 'Connected issues; causes supported; fact and judgement separated'],
      ['Commercial judgement', '20%', 'Priorities follow impact; capex weighed against cash; temporary vs structural'],
      ['Quality of recommendations', '20%', 'Three actions with owner, target, date and financial effect'],
      ['Communication', '10%', 'Clear headline, plain language, the right few numbers, fits five minutes'],
    ], 18),
    notes: 'Pass mark 70%. Bands: Excellent 85 to 100% of a criterion\'s weight, Competent 70 to 84%, Not yet competent below 70%. Second-mark any submission within 5 points of the pass mark and any Excellent overall. Never share the facilitator marking guide with learners.',
  },
  {
    id: 96, layout: 'exercise', module: 11,
    kicker: 'Capstone · Block 2', meta: 'Teams · 90 min', title: 'Finish, then present',
    steps: [
      { title: 'Finish the analysis', detail: '45 minutes to complete tasks and agree the recommendation.' },
      { title: 'Present in five minutes', detail: 'One speaker per team, timed. Headline first, ask last.' },
      { title: 'Give and take feedback', detail: 'One strength and one improvement per team, quoting their numbers.' },
    ],
    checkFor: 'the summary leads with the headline, uses a few decisive numbers in rand, stays consistent with the analysis and ends with a clear ask.',
    notes: 'Keep strict time on each summary. Listen for the FY2027 outlook: EBITDA tracking about R1.3m below budget before the September news and about R1.47m after it. Mark down actions that contradict the analysis, such as a larger dividend to reward growth.',
  },
  {
    id: 97, layout: 'outcome', module: 11,
    kicker: 'Programme close', title: 'Three days: what you can now do',
    checks: [
      'Read the three statements as one connected story',
      'Explain why profit and cash differ, and spot a cash squeeze',
      'Use margins, ratios and working capital days as questions',
      'Explain variances and judge an investment on NPV and payback',
      'Update a forecast and test scenarios and sensitivities',
      'Challenge a management pack and present a grounded recommendation',
    ],
    footnote: 'Certificate: every lesson, module check and the final assessment complete. Submit your Manager Financial Review by the agreed deadline.',
    notes: '15 minutes, 16:15 to 16:30. Explain the certification rules: module checks and the final assessment at 70%, plus the capstone. Confirm the individual submission deadline set for this cohort, then run the evaluation. Thank the group and remind them the lessons, calculators and templates stay available in Okiru Learn.',
  },
];
